import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import FormControl from "../../components/common/FormControl";
import { MODULE_CONFIG } from "./shared";
import {
  capitalize,
  capitalizeSentence,
  formatDate,
  formatTime,
  getInitials,
} from "../../methods/methods";
import {
  FiArrowLeft,
  FiMail,
  FiPhone,
  FiClock,
  FiSend,
  FiTrash2,
  FiCheckCircle,
  FiCornerUpLeft,
  FiX,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function ContactUsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const logedInUser = JSON.parse(
    sessionStorage.getItem("logedInUser") || JSON.stringify(user || {})
  );

  // Dynamic permission checks for staff
  const isSuperAdmin = user?.isSuperAdmin || !user?.subRole;
  const staffPermissions =
    (logedInUser?.subRole &&
      logedInUser?.subRole?.permissions &&
      logedInUser?.subRole?.permissions[0]) ||
    (user?.subRole && user?.subRole?.permissions && user?.subRole?.permissions[0]) ||
    {};

  const hasStaffPermission = (key) => {
    if (isSuperAdmin) return true;
    if (!key) return true;
    return Boolean(staffPermissions[key]);
  };

  const canChangeStatus =
    isSuperAdmin ||
    hasStaffPermission("contactUs_edit") ||
    hasStaffPermission("contactUs");
  const canReply =
    isSuperAdmin ||
    hasStaffPermission("contactUs_reply") ||
    hasStaffPermission("contactUs_edit") ||
    hasStaffPermission("contactUs");
  const canDelete =
    isSuperAdmin ||
    hasStaffPermission("contactUs_delete");

  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Fetch inquiry detail & auto mark as read
  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    loader(true);

    try {
      const endpoint = `${MODULE_CONFIG.endpoints.detail}?id=${id}&markAsRead=true`;
      const res = await ApiClient.get(endpoint);

      if (res?.success && res?.data) {
        const data = res.data;
        setInquiry(data);
        const customerDisplayName =
          data.fullName?.trim() ||
          data.name?.trim() ||
          (data.email ? data.email.split("@")[0] : "Customer");
        setReplySubject(`Re: Regarding your inquiry - Charles Smith Associates`);
      } else {
        toast.error("Unable to find inquiry record.");
      }
    } catch (err) {
      console.error("Failed to fetch inquiry detail:", err);
      toast.error("Failed to load inquiry details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Handle Status Change (Strictly Read / Unread toggle without dropdown)
  const handleStatusChange = async (newStatus) => {
    if (!id || !newStatus) return;
    loader(true);

    try {
      const res = await ApiClient.put(
        `${MODULE_CONFIG.endpoints.statusChange}?id=${id}&status=${newStatus}`
      );
      if (res?.success) {
        toast.success(`Status updated to ${capitalize(newStatus)}.`);
        setInquiry((prev) => (prev ? { ...prev, status: newStatus } : prev));
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      loader(false);
    }
  };

  // Open Reply Modal
  const openReplyModal = () => {
    const customerDisplayName =
      inquiry?.fullName?.trim() ||
      inquiry?.name?.trim() ||
      (inquiry?.email ? inquiry?.email.split("@")[0] : "Customer");
    setReplySubject(`Re: Regarding your inquiry - Charles Smith Associates`);
    setReplyMessage("");
    setReplyModalOpen(true);
  };

  // Submit Reply with exact API payload structure
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message.");
      return;
    }
    setSendingReply(true);
    loader(true);

    try {
      const payload = {
        contactUsIds: [id],
        subject: replySubject.trim(),
        message: replyMessage.trim(),
      };
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.reply, payload);
      if (res?.success) {
        toast.success("Reply successfully sent to customer.");
        setReplyMessage("");
        setReplyModalOpen(false);
        fetchDetail();
      }
    } catch (err) {
      console.error("Failed to send reply:", err);
    } finally {
      setSendingReply(false);
      loader(false);
    }
  };

  // Handle Delete Inquiry
  const handleDelete = () => {
    const customerDisplayName =
      inquiry?.fullName?.trim() ||
      inquiry?.name?.trim() ||
      (inquiry?.email ? inquiry?.email.split("@")[0] : "Customer");

    swal({
      title: "Delete Inquiry?",
      text: `This customer inquiry from ${capitalize(customerDisplayName)} will be permanently deleted.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.delete(
          `${MODULE_CONFIG.endpoints.delete}?id=${id}`
        );
        if (res?.success) {
          toast.success("Inquiry deleted successfully.");
          navigate(MODULE_CONFIG.routeBase);
        }
      } catch (err) {
        console.error("Failed to delete inquiry:", err);
      } finally {
        loader(false);
      }
    });
  };

  const customerDisplayName =
    inquiry?.fullName?.trim() ||
    inquiry?.name?.trim() ||
    (inquiry?.email ? inquiry?.email.split("@")[0] : "Customer");
  const customerName = capitalize(customerDisplayName);

  const tenantOrg = capitalize(
    inquiry?.tenantName ||
    inquiry?.organization ||
    inquiry?.companyName ||
    inquiry?.tenantId?.name ||
    "Global / Direct Platform"
  );

  const badgeInfo =
    MODULE_CONFIG.statusMap[inquiry?.status] || MODULE_CONFIG.statusMap.unread;

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-12 antialiased">

        {/* ======================================================== */}
        {/* 1. TOP HEADER & HIGH-CONTRAST ACTION BUTTONS */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs"
              title="Back to Inquiries List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                Inquiry Details
              </h1>
              <p className="text-xs font-bold text-slate-700">
                Customer Dossier & Response Management
              </p>
            </div>
          </div>

          {/* Top Actions: Send Reply Modal Trigger & Delete */}
          <div className="flex items-center gap-3 self-stretch sm:self-auto justify-end flex-wrap">
            {canReply && (
              <button
                type="button"
                onClick={openReplyModal}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#115B9D] hover:bg-[#0E528F] active:bg-[#0A3E6E] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
              >
                <FiSend className="w-3.5 h-3.5" />
                <span>Send Reply</span>
              </button>
            )}

            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100 text-xs font-bold transition-all cursor-pointer shadow-xs"
              >
                <FiTrash2 className="w-3.5 h-3.5 text-rose-700" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && !inquiry ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-slate-300 shadow-sm">
            <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs font-bold text-slate-900">Loading inquiry information...</p>
          </div>
        ) : null}

        {inquiry ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 items-start">

            {/* ======================================================== */}
            {/* 2. LEFT / MAIN COLUMN: INQUIRY DOSSIER & SENT REPLIES */}
            {/* ======================================================== */}
            <div className="lg:col-span-2 space-y-5 sm:space-y-6">

              {/* Inquiry Message Dossier (High contrast, no light grey) */}
              <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">

                {/* Customer Identity Bar */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-200 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-300 text-[#115B9D] font-black text-base flex items-center justify-center shrink-0 shadow-xs">
                      {getInitials(customerName)}
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                        {customerName}
                      </h2>
                      <p className="text-xs text-slate-700 font-bold">
                        Submitted on {formatDate(inquiry.createdAt)} at {formatTime(inquiry.createdAt)}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wide border ${badgeInfo.bgClass}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${badgeInfo.dotClass}`} />
                    {badgeInfo.label}
                  </span>
                </div>

                {/* Key Metadata Grid (High Contrast) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 p-4 rounded-xl bg-blue-50/30 border border-slate-300 text-xs">
                  <div>
                    <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                      Email Address:
                    </span>
                    <a
                      href={`mailto:${inquiry.email}`}
                      className="font-bold text-slate-950 hover:text-[#115B9D] break-all inline-flex items-center gap-1.5 mt-1"
                    >
                      <FiMail className="w-4 h-4 text-[#115B9D] shrink-0" />
                      <span>{inquiry.email || "-"}</span>
                    </a>
                  </div>

                  <div>
                    <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                      Phone / Mobile:
                    </span>
                    <span className="font-bold text-slate-950 inline-flex items-center gap-1.5 mt-1">
                      <FiPhone className="w-4 h-4 text-[#115B9D] shrink-0" />
                      <span>{inquiry.phone || inquiry.mobile || "Not provided"}</span>
                    </span>
                  </div>

                  <div className="sm:col-span-2 pt-2.5 border-t border-slate-300">
                    <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                      Associated Tenant Organization:
                    </span>
                    <span className="font-black text-slate-950 inline-flex items-center gap-1.5 mt-1">
                      <HiOutlineBuildingOffice2 className="w-4 h-4 text-[#115B9D]" />
                      <span>{tenantOrg}</span>
                    </span>
                  </div>
                </div>

                {/* Message Body Content */}
                <div className="space-y-2">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-900">
                    Message Body
                  </h3>
                  <div className="p-4 rounded-xl bg-blue-50/20 border border-slate-300 text-slate-900 text-sm font-semibold leading-relaxed whitespace-pre-line">
                    {capitalizeSentence(inquiry.message || inquiry.description || "No message content provided.")}
                  </div>
                </div>

                {/* Past Sent Response (if replied) */}
                {inquiry.replyMessage || (Array.isArray(inquiry.replies) && inquiry.replies.length > 0) ? (
                  <div className="space-y-2.5 pt-3 border-t border-slate-200">
                    <h3 className="text-xs font-black uppercase tracking-wider text-emerald-900 flex items-center gap-1.5">
                      <FiCornerUpLeft className="w-4 h-4 text-emerald-700" />
                      Sent Response History
                    </h3>
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-300 text-slate-900 text-xs font-medium space-y-1.5">
                      <div className="font-black text-emerald-950 text-sm">
                        {inquiry.replySubject || "Platform Admin Reply"}
                      </div>
                      <p className="whitespace-pre-line text-slate-900 leading-relaxed font-semibold">
                        {inquiry.replyMessage || (inquiry.replies && inquiry.replies[0]?.message)}
                      </p>
                      {inquiry.repliedAt && (
                        <div className="text-[11px] text-emerald-800 font-bold pt-1 border-t border-emerald-200">
                          Dispatched: {formatDate(inquiry.repliedAt)} at {formatTime(inquiry.repliedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="pt-2 flex items-center justify-between p-3.5 rounded-xl border border-dashed border-slate-300 bg-blue-50/20 flex-wrap gap-2">
                    <span className="text-xs font-bold text-slate-800">
                      No response has been sent to this inquiry yet.
                    </span>
                    {canReply && (
                      <button
                        type="button"
                        onClick={openReplyModal}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#115B9D] hover:bg-[#0E528F] text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
                      >
                        <FiSend className="w-3.5 h-3.5" />
                        <span>Send Reply Now</span>
                      </button>
                    )}
                  </div>
                )}

              </div>

            </div>

            {/* ======================================================== */}
            {/* 3. RIGHT COLUMN: STATUS CONTROLS (NO DROPDOWN) & AUDIT */}
            {/* ======================================================== */}
            <div className="space-y-5 sm:space-y-6">

              {/* Status Control Tile (Single Action Toggle, NO Dropdown) */}
              <div className="bg-white rounded-2xl p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-200">
                  Inquiry Status
                </h3>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Current Status:</span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase border tracking-wide ${badgeInfo.bgClass}`}>
                    <span className={`w-2 h-2 rounded-full ${badgeInfo.dotClass}`} />
                    {badgeInfo.label}
                  </span>
                </div>

                {canChangeStatus && (
                  <div className="pt-2 space-y-2">
                    {inquiry.status === "read" ? (
                      <button
                        type="button"
                        onClick={() => handleStatusChange("unread")}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-amber-50 hover:bg-amber-100 text-amber-950 border border-amber-400 transition-all cursor-pointer shadow-xs"
                      >
                        <FiClock className="w-4 h-4 text-amber-800" />
                        <span>Mark as Unread</span>
                      </button>
                    ) : inquiry.status === "unread" ? (
                      <button
                        type="button"
                        onClick={() => handleStatusChange("read")}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-emerald-600 hover:bg-emerald-700 text-white transition-all cursor-pointer shadow-sm shadow-emerald-600/20"
                      >
                        <FiCheckCircle className="w-4 h-4" />
                        <span>Mark as Read</span>
                      </button>
                    ) : (
                      /* If status is replied, user can choose Mark as Unread or Mark as Read */
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleStatusChange("unread")}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs bg-amber-50 hover:bg-amber-100 text-amber-950 border border-amber-400 transition-all cursor-pointer"
                        >
                          <FiClock className="w-3.5 h-3.5 text-amber-800" />
                          <span>Mark Unread</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStatusChange("read")}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-950 border border-emerald-400 transition-all cursor-pointer"
                        >
                          <FiCheckCircle className="w-3.5 h-3.5 text-emerald-800" />
                          <span>Mark Read</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Organization & Audit Information */}
              <div className="bg-white rounded-2xl p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-3.5 text-xs">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-200">
                  Audit Information
                </h3>

                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 font-bold">Submitted Date:</span>
                    <span className="text-slate-950 font-black">
                      {formatDate(inquiry.createdAt)} {formatTime(inquiry.createdAt)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 font-bold">Last Updated:</span>
                    <span className="text-slate-950 font-black">
                      {formatDate(inquiry.updatedAt || inquiry.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => navigate(MODULE_CONFIG.routeBase)}
                    className="w-full py-2.5 px-3 rounded-xl border border-slate-400 bg-white hover:bg-slate-100 text-slate-900 font-bold text-xs text-center transition-all block shadow-xs cursor-pointer"
                  >
                    Back to All Inquiries
                  </button>
                </div>
              </div>

            </div>

          </div>
        ) : null}

      </div>

      {/* ======================================================== */}
      {/* 4. SEND REPLY MODAL (NO DIRECT INLINE SECTION) */}
      {/* ======================================================== */}
      <Transition appear show={replyModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[99999]"
          onClose={() => setReplyModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[99999]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto z-[100000]">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all border border-slate-300">
                  {/* Clean Modal Header */}
                  <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-300 text-[#115B9D] flex items-center justify-center">
                        <FiSend className="w-4 h-4" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-black text-slate-900">
                          Reply to Customer Inquiry
                        </Dialog.Title>
                        <p className="text-[11px] font-bold text-slate-700">
                          Dispatches official email response to customer
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyModalOpen(false)}
                      className="p-1.5 rounded-lg text-slate-600 hover:text-slate-950 hover:bg-slate-200 transition-colors cursor-pointer"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Modal Body Form */}
                  <form onSubmit={handleSendReply} className="p-6 space-y-4 text-xs">
                    {/* Customer Context Dossier Card */}
                    <div className="p-3.5 rounded-xl bg-blue-50/40 border border-slate-300 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-700">
                            Recipient:
                          </span>
                          <span className="font-black text-slate-950">
                            {customerName}
                          </span>
                        </div>
                        <span className="font-bold text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-300 text-[11px]">
                          {inquiry?.email || "-"}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-900 font-semibold bg-white p-2.5 rounded-lg border border-slate-300 italic line-clamp-2">
                        "{inquiry?.message || inquiry?.description || "No message content"}"
                      </div>
                    </div>

                    {/* Subject Line with platform FormControl */}
                    <div>
                      <FormControl
                        type="text"
                        label="Subject Line"
                        required
                        value={replySubject}
                        onChange={(val) => setReplySubject(val)}
                        placeholder="Enter subject..."
                      />
                    </div>

                    {/* Reply Message with platform FormControl */}
                    <div>
                      <FormControl
                        type="textarea"
                        label="Reply Message"
                        required
                        value={replyMessage}
                        onChange={(val) => setReplyMessage(val)}
                        placeholder="Type your official response to this customer inquiry..."
                      />
                    </div>

                    {/* Modal Footer Bar */}
                    <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2.5">
                      <button
                        type="button"
                        onClick={() => setReplyModalOpen(false)}
                        className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-800 hover:bg-slate-100 font-bold transition-all cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={sendingReply}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#115B9D] hover:bg-[#0E528F] active:bg-[#0A3E6E] text-white font-bold transition-all shadow-md shadow-[#115B9D]/25 disabled:opacity-60 cursor-pointer"
                      >
                        <FiSend className={`w-3.5 h-3.5 ${sendingReply ? "animate-spin" : ""}`} />
                        <span>{sendingReply ? "Dispatching..." : "Send Reply"}</span>
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
